/* eslint-disable react/prop-types */
import {
    Callout,
    Checkbox,
    classNamesFunction,
    DirectionalHint,
    ICheckboxProps,
    IProcessedStyleSet,
    IStyleSet,
    Label,
    mergeStyles,
    Stack,
    styled,
    Text,
    TooltipHost,
    TooltipOverflowMode
} from "@fluentui/react"
import { EditIcon } from "@fluentui/react-icons-mdl2"
import React, { useState } from "react"

import { ParameterTO, Value } from "@encoway/c-services-js-client"
import { L10n } from "@encoway/l10n"
import { ComponentFactory } from "@encoway/react-configurator"

import {
    ComponentName,
    READY_STATE_NOT_READY,
    SPEC_STATE_SET_BY_SYSTEM
} from "../../constants"
import { MultiValueCheckboxStyles } from "./MultiValueCheckbox.styles"
import {
    IMultiValueCheckboxStyles,
    MultiValueCheckboxProps
} from "./MultiValueCheckbox.types"

/**
 * The MultiValueCheckbox component renders a ParameterTO as a headline using
 * the Parameter component and its values are rendered as ValueCheckbox components.
 *
 * Links:
 * - [MS Fluent Checkbox](https://developer.microsoft.com/de-DE/fluentui#/controls/web/checkbox)
 *
 * @visibleName MultiValueCheckbox
 */
function IMultiValueCheckbox(props: MultiValueCheckboxProps) {
    const { styles, theme, ...delegatedProps } = props
    const classNames: IProcessedStyleSet<IStyleSet<IMultiValueCheckboxStyles>> =
        classNamesFunction()(styles, theme)

    const parameterComponent = ComponentFactory.instanceOf(
        ComponentName.Parameter,
        {
            ...delegatedProps,
            hideStateIcon: props.data.readyState === "READY"
        }
    )

    let rootClassName = classNames.root
    if (props.data.readyState === READY_STATE_NOT_READY) {
        rootClassName = mergeStyles(rootClassName, classNames.mandatoryRoot)
    }

    return (
        <Stack className={rootClassName}>
            {parameterComponent}
            <Stack tokens={{ childrenGap: "0.5em" }}>
                {!props.data.values ? (
                    <></>
                ) : (
                    props.data.values.map((value, index) => (
                        <ValueCheckbox
                            key={props.data.id + index}
                            id={props.data.id + index}
                            parameter={props}
                            value={value}
                            classNames={classNames}
                        />
                    ))
                )}
            </Stack>
        </Stack>
    )
}
interface InfoButtonProps {
    parameter: MultiValueCheckboxProps
    value: Value
}

function InfoButton({ parameter, value }: InfoButtonProps) {
    return ComponentFactory.instanceOf(ComponentName.InfoButton, {
        ...parameter,
        styles: { root: { padding: 0 } },
        data: value
    })
}

interface StateIconProps {
    id: string
    value: Value
}

function StateIcon({ id, value }: StateIconProps) {
    return ComponentFactory.instanceOf(ComponentName.StateIcon, {
        data: {
            id,
            values: [
                {
                    ...value
                }
            ]
        } as ParameterTO
    })
}

interface AdditionalValueIconProps {
    iconID: string
    classNames: IProcessedStyleSet<IMultiValueCheckboxStyles>
}

function AdditionalValueIcon({ iconID, classNames }: AdditionalValueIconProps) {
    const [calloutVisible, setCalloutVisible] = useState(false)
    return (
        <>
            <EditIcon
                id={iconID}
                className={classNames.additionalValueIcon}
                onClick={() => setCalloutVisible(!calloutVisible)}
            />
            {calloutVisible && (
                <Callout
                    target={`#${iconID}`}
                    onDismiss={() => setCalloutVisible(false)}
                    directionalHint={DirectionalHint.topAutoEdge}
                    setInitialFocus
                    className={classNames.additionalValueIconCallout}
                >
                    <Text>
                        {L10n.format(
                            "Configuration.AdditionalValue.MultiValue.Name"
                        )}
                    </Text>
                </Callout>
            )}
        </>
    )
}

function BurgerMenu(props: MultiValueCheckboxProps) {
    return ComponentFactory.instanceOf(ComponentName.BurgerMenu, {
        ...props,
        onlyPlaceholder: true
    })
}

/**
 * Props for the ValueCheckbox component used to represent
 * each value in the MultiValueCheckbox component.
 */
interface ValueCheckboxProps {
    /**
     * ID for the value.
     */
    id: string
    /**
     * These props are used to access certain data from the parameter.
     */
    parameter: MultiValueCheckboxProps
    /**
     * The value that we want to display as a checkbox.
     */
    value: Value
    /**
     * The classNames that are used to style elements related to the value.
     */
    classNames: IProcessedStyleSet<IStyleSet<IMultiValueCheckboxStyles>>
}

/**
 * The ValueCheckbox component that renders a checkbox
 * with a StateIcon and an optional InfoButton for a value.
 */
function ValueCheckbox({
    id,
    parameter,
    value,
    classNames
}: ValueCheckboxProps) {
    const disabled = !parameter.data.editable as boolean
    const readOnly = parameter.options?.readOnly as boolean
    const excluded = !value.enabled

    let checkBoxClassName = classNames.checkbox
    if (readOnly) {
        checkBoxClassName = mergeStyles(checkBoxClassName, classNames.disabled)
    } else {
        if (value.selectionSource === SPEC_STATE_SET_BY_SYSTEM) {
            checkBoxClassName = mergeStyles(
                checkBoxClassName,
                classNames.system
            )
        }

        if (excluded) {
            checkBoxClassName = mergeStyles(
                checkBoxClassName,
                classNames.disabled
            )
        }
    }

    function onChange() {
        parameter.onValueChanged(parameter.data, value.value)
    }

    function renderLabel(
        classNames: IProcessedStyleSet<IStyleSet<IMultiValueCheckboxStyles>>,
        props: ICheckboxProps
    ) {
        return (
            <Stack
                horizontal
                tokens={{ childrenGap: "10px" }}
                onClick={(e) => e.preventDefault()}
                style={{ minWidth: 0 }}
            >
                <Label
                    disabled={disabled}
                    className={classNames.label}
                    onClick={disabled ? () => null : onChange}
                    style={disabled ? {} : { cursor: "pointer" }}
                >
                    <TooltipHost
                        hostClassName={classNames.textContainer}
                        overflowMode={TooltipOverflowMode.Self}
                        content={props.label}
                    >
                        {props.label}
                    </TooltipHost>
                </Label>
            </Stack>
        )
    }

    return (
        <Stack
            horizontal
            tokens={{ childrenGap: "9px" }}
            className={classNames.checkboxContainer}
        >
            <Checkbox
                className={checkBoxClassName}
                disabled={disabled || readOnly}
                label={value.translatedValue}
                boxSide={parameter.checkboxPosition || "start"}
                onChange={onChange}
                checked={value.selected}
                onRenderLabel={(props) => {
                    if (!props) {
                        console.error(
                            "onRenderLabel: checkbox props are undefined"
                        )
                        return <></>
                    }
                    return renderLabel(classNames, props)
                }}
            />
            {value.properties.isAdditional && (
                <AdditionalValueIcon
                    iconID={`iconID${id}`}
                    classNames={classNames}
                />
            )}
            <InfoButton parameter={parameter} value={value} />
            <Stack.Item grow className={classNames.stateIconContainer}>
                <StateIcon id={id} value={value} />
            </Stack.Item>
            <BurgerMenu {...parameter} />
        </Stack>
    )
}

export const MultiValueCheckbox = styled(
    IMultiValueCheckbox,
    MultiValueCheckboxStyles
)
MultiValueCheckbox.displayName = "MultiValueCheckbox"
